/**
 * The star scale. One rating scale for the whole app.
 *
 * Every rating in Punchboard — the headline "would you play it again" and every
 * aspect — is an integer 1..10 counting *half stars*, so the person sees 0.5 to
 * 5.0 stars and the database stores a small integer with no fractions in it.
 * Halves are the reason for the doubling: `3.5` cannot be stored as an integer
 * and `7` can, and a float column would invite a 3.47 that nobody chose.
 *
 * This replaces the earlier arrangement, where a rating was one of a rubric's
 * `Stop` values — 1..10 for the headline, -2..+2 for a bipolar aspect, 1..5 for
 * a unipolar one. Three domains meant three validation rules, three renderers
 * and a migration every time a rubric was edited. One domain means the control,
 * the CHECK constraint and the aggregate all agree by construction.
 *
 * ## Stops did not go away
 *
 * The rubric's written stops — "Pure luck", "Happy to", "Brain-melting" — are no
 * longer what a rating *is*, but they are still the only words this app has for
 * what a rating *means*, and the results screens are built out of them. So a
 * rubric is now vocabulary rather than domain: `stopForStar` maps a star value
 * onto the stop it lands nearest, and the summary sentences carry on reading the
 * way they always did.
 *
 * The mapping spreads a rubric's stops evenly across the full 1..10 range, ends
 * included, so the bottom stop is 0.5 stars and the top stop is 5. It is
 * deliberately reversible: `stopIndexForStar(starForStopIndex(i, n), n) === i`
 * for every stop of every rubric size, which is what makes the one-off migration
 * of already-stored ratings lossless in the direction that matters.
 *
 * A ten-stop rubric — which is exactly the headline — maps 1:1, so no headline
 * rating ever moved.
 */

import type { Stop } from "./types.ts";

/** Lowest storable rating: half a star. */
export const STAR_MIN = 1;
/** Highest storable rating: five stars. */
export const STAR_MAX = 10;
/** Stars drawn on screen. The stored value counts halves of these. */
export const STAR_COUNT = 5;

/** A stored rating (1..10) as the number of stars a person sees (0.5..5). */
export function starsOf(value: number): number {
  return value / 2;
}

/** The value of a whole star: two half-stars each, so star 3 is 6. */
export function fullValue(star: number): number {
  return star * 2;
}

/**
 * A rating's position along an axis that spans the whole scale, 0..1.
 *
 * Deliberately not the same sum as the star row's fill. The fill is a *width* —
 * five stars filled is the whole row, so it divides by `STAR_MAX`. This is a
 * *position* on an axis whose ends are the lowest and highest ratings that
 * exist, so half a star sits at 0 rather than at a tenth. Getting the two
 * confused is what put the results rail's notches a whole notch away from the
 * dots they were supposed to sit under.
 */
export function starFraction(value: number): number {
  const span = STAR_MAX - STAR_MIN;
  return Math.min(Math.max((value - STAR_MIN) / span, 0), 1);
}

/** True for a value this app is willing to store as a rating. */
export function isStarValue(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= STAR_MIN && value <= STAR_MAX;
}

/** Force a number onto the scale. Used at boundaries, never to fix up intent. */
export function clampStar(value: number): number {
  return Math.min(Math.max(Math.round(value), STAR_MIN), STAR_MAX);
}

/**
 * "3.5" — the rating as a person would say it, with no trailing `.0`.
 *
 * Not "3.5/5" and not "3.5 stars": the stars are drawn right next to it, and
 * every caller that needs the longer form is announcing to a screen reader,
 * where `starsAnnouncement` is the one that says it in full.
 *
 * Overloaded so a caller holding a number gets a `string` back and never has to
 * invent a fallback for a null this function cannot return. Two of those
 * fallbacks used to read `?? String(value)`, which prints the stored half-star
 * integer — the doubled figure ("mean 6.0" for three stars) this function exists
 * to eliminate. They were unreachable, which is worse than wrong: nothing would
 * have caught them going live.
 *
 * Also used on *differences* — a spread, `SPLIT_THRESHOLD` — and that is sound
 * only because `starsOf` is linear. Anything less than a pure halving here
 * (clamping to `STAR_MIN`, rounding, appending a glyph) silently corrupts every
 * gap on screen, so keep the two apart if that day comes.
 */
export function formatStars(value: number): string;
export function formatStars(value: number | null): string | null;
export function formatStars(value: number | null): string | null {
  if (value === null) return null;
  const stars = starsOf(value);
  return Number.isInteger(stars) ? String(stars) : stars.toFixed(1);
}

/** The `aria-valuetext` form. Screen readers get the unit spelled out. */
export function starsAnnouncement(value: number | null): string {
  if (value === null) return "Not rated";
  const stars = starsOf(value);
  return `${formatStars(value)} out of ${STAR_COUNT} star${stars === 1 ? "" : "s"}`;
}

// ---------------------------------------------------------------------------
// Rubric vocabulary
// ---------------------------------------------------------------------------

/**
 * The star value a rubric's stop `i` sits on, for a rubric of `n` stops.
 *
 * Ends are pinned — the first stop is always 1 and the last is always 10 — so
 * every rubric spans the whole track whatever its length, and the extremes of
 * the written ladder still mean the extremes of the scale.
 */
export function starForStopIndex(index: number, stopCount: number): number {
  if (stopCount <= 1) return STAR_MAX;
  const span = STAR_MAX - STAR_MIN;
  return clampStar(STAR_MIN + Math.round((index * span) / (stopCount - 1)));
}

/** The stop index a star value lands nearest, for a rubric of `n` stops. */
export function stopIndexForStar(value: number, stopCount: number): number {
  if (stopCount <= 1) return 0;
  const span = STAR_MAX - STAR_MIN;
  const raw = Math.round(((value - STAR_MIN) * (stopCount - 1)) / span);
  return Math.min(Math.max(raw, 0), stopCount - 1);
}

/**
 * The stop a rating lands on, in the rubric's own order.
 *
 * `stops` is taken in the order the rubric declares, which is track order — the
 * same order the editor shows and the same order a session freezes. It is not
 * re-sorted by `value` here, because a stop's numeric value no longer orders
 * anything; after the move to stars it is a leftover identifier.
 */
export function stopForStar(stops: readonly Stop[], value: number | null): Stop | null {
  if (value === null || stops.length === 0) return null;
  return stops[stopIndexForStar(value, stops.length)] ?? null;
}

/** The written label for a rating, or null when there is nothing to say. */
export function starLabel(stops: readonly Stop[], value: number | null): string | null {
  return stopForStar(stops, value)?.label ?? null;
}
